import Joi from "joi";
import { TFunction } from "i18next";
import { RegistrationFormData } from "../types";
import { RICH_TEXT_LIMITS, richTextRule } from "../../../../../utils/richText";

const emailContentMessages = (t: TFunction) => ({
  "string.empty": t("customRegistration.validation.emailContentRequired"),
  "richText.tooShort": t("customRegistration.validation.emailContentMinLength"),
  "richText.tooLong": t("customRegistration.validation.emailContentMaxLength", {
    max: RICH_TEXT_LIMITS.welcomeEmail,
  }),
  "richText.tooHeavy": t("htmlEditor.tooHeavy"),
});

const fieldConfigSchema = Joi.object({
  visible: Joi.boolean().required(),
  required: Joi.boolean().required(),
}).custom((value, helpers) => {
  if (value.required && !value.visible) {
    return helpers.error("any.invalid");
  }
  return value;
});

const phoneFieldConfigSchema = Joi.object({
  visible: Joi.boolean().required(),
  required: Joi.boolean().required(),
  requireVerification: Joi.boolean().optional(),
}).custom((value, helpers) => {
  if (value.required && !value.visible) {
    return helpers.error("any.invalid");
  }
  if (value.requireVerification && (!value.required || !value.visible)) {
    return helpers.error("any.invalid");
  }
  return value;
});

export const createValidationSchema = (t: TFunction) =>
  Joi.object<RegistrationFormData>({
    requirePassword: Joi.boolean().required(),
    formDescription: Joi.string().allow("").max(200).optional(),
    fields: Joi.object({
      user_last_name: fieldConfigSchema,
      user_first_name: fieldConfigSchema,
      user_phone: phoneFieldConfigSchema,
      user_country_code: fieldConfigSchema,
      user_user_name: fieldConfigSchema,
      user_birth_date: fieldConfigSchema,
    }).required(),
    customQuestions: Joi.array()
      .items(
        Joi.object({
          question: Joi.string()
            .trim()
            .min(3)
            .max(300)
            .required()
            .messages({
              "string.empty": t(
                "customRegistration.validation.questionEmpty"
              ) as string,
              "string.min": t(
                "customRegistration.validation.questionMinLength"
              ) as string,
              "string.max": t(
                "customRegistration.validation.questionMaxLength"
              ) as string,
            }),
          fieldType: Joi.string()
            .valid(
              "text",
              "textarea",
              "date",
              "datetime-local",
              "time",
              "number",
              "file",
              "radio",
              "checkbox"
            )
            .required()
            .messages({
              "any.only": t(
                "customRegistration.validation.invalidFieldType"
              ) as string,
              "any.required": t(
                "customRegistration.validation.fieldTypeRequired"
              ) as string,
            }),
          options: Joi.when("fieldType", {
            is: Joi.string().valid("radio", "checkbox"),
            then: Joi.array()
              .items(Joi.string().allow("").max(100))
              .max(10)
              .custom((value, helpers) => {
                const nonEmptyOptions = (value || []).filter(
                  (opt: string) => opt && opt.trim().length > 0
                );

                if (nonEmptyOptions.length < 2) {
                  return helpers.error("array.min", { limit: 2 });
                }

                if (nonEmptyOptions.length > 10) {
                  return helpers.error("array.max", { limit: 10 });
                }

                return value;
              })
              .messages({
                "array.min": t(
                  "customRegistration.validation.minOptionsRequired"
                ) as string,
                "array.max": t("customRegistration.messages.maxOptions", {
                  max: 10,
                }) as string,
              }),
            otherwise: Joi.optional(),
          }),
          required: Joi.boolean().optional(),
        })
      )
      .max(15)
      .messages({
        "array.max": t(
          "customRegistration.validation.maxQuestionsReached"
        ) as string,
      }),
    customQuestionsPosition: Joi.string().valid("first", "last").required(),
    postRegistrationAction: Joi.string()
      .valid("toast", "redirect")
      .required()
      .messages({
        "any.only": t("customRegistration.validation.invalidAction") as string,
      }),
    redirectUrl: Joi.string()
      .uri({ scheme: ["http", "https"] })
      .allow("")
      .when("postRegistrationAction", {
        is: "redirect",
        then: Joi.string()
          .uri({ scheme: ["http", "https"] })
          .required()
          .messages({
            "string.empty": t(
              "customRegistration.validation.redirectUrlRequired"
            ) as string,
            "string.uri": t(
              "customRegistration.validation.invalidUrl"
            ) as string,
          }),
      }),
    paymentConfig: Joi.object({
      enabled: Joi.boolean().required(),
      paymentType: Joi.string()
        .valid("one-time", "recurring")
        .default("one-time"),
      description: Joi.string().allow("").max(200),
      amount: Joi.number().min(0).max(1000000),
      currency: Joi.string().valid("EUR", "USD", "CHF"),
      plan_code: Joi.string().allow("").optional(),
    }).when(".enabled", {
      is: true,
      then: Joi.object({
        enabled: Joi.boolean().valid(true).required(),
        paymentType: Joi.string().valid("one-time", "recurring").required(),
        // Both kinds show it to the customer: registration refuses a payment without it.
        description: Joi.string()
          .invalid("")
          .min(2)
          .max(200)
          .required()
          .messages({
            "any.invalid": t(
              "customRegistration.validation.paymentDescriptionRequired"
            ) as string,
            "string.empty": t(
              "customRegistration.validation.paymentDescriptionRequired"
            ) as string,
            "string.min": t(
              "customRegistration.validation.paymentDescriptionMinLength"
            ) as string,
            "string.max": t(
              "customRegistration.validation.paymentDescriptionMaxLength"
            ) as string,
          }),
        amount: Joi.when("paymentType", {
          is: "one-time",
          then: Joi.number()
            .integer()
            .min(100)
            .max(1000000)
            .required()
            .messages({
              "number.min": t(
                "customRegistration.validation.paymentAmountMin"
              ) as string,
              "number.max": t(
                "customRegistration.validation.paymentAmountMax"
              ) as string,
              "number.base": t(
                "customRegistration.validation.paymentAmountRequired"
              ) as string,
              "any.required": t(
                "customRegistration.validation.paymentAmountRequired"
              ) as string,
              "number.unsafe": t(
                "customRegistration.validation.paymentAmountRequired"
              ) as string,
              "number.integer": t(
                "customRegistration.validation.paymentAmountRequired"
              ) as string,
            }),
          otherwise: Joi.number().optional(),
        }),
        currency: Joi.when("paymentType", {
          is: "one-time",
          then: Joi.string()
            .valid("EUR", "USD", "CHF")
            .required()
            .messages({
              "any.only": t(
                "customRegistration.validation.paymentCurrencyInvalid"
              ) as string,
            }),
          otherwise: Joi.string().valid("EUR", "USD", "CHF").optional(),
        }),
        plan_code: Joi.when("paymentType", {
          is: "recurring",
          then: Joi.string()
            .invalid("")
            .required()
            .messages({
              "any.invalid": t(
                "customRegistration.validation.paymentPlanRequired"
              ) as string,
              "string.empty": t(
                "customRegistration.validation.paymentPlanRequired"
              ) as string,
              "any.required": t(
                "customRegistration.validation.paymentPlanRequired"
              ) as string,
            }),
          otherwise: Joi.string().allow("").optional(),
        }),
      }),
    }),
    automatedEmail: Joi.object({
      enabled: Joi.boolean().required(),
      sendDelay: Joi.string()
        .valid("immediate", "1hour", "6hours", "1day", "2days")
        .default("immediate"),
      subject: Joi.string().allow("").max(200),
      content: Joi.string()
        .allow("")
        .custom(richTextRule(RICH_TEXT_LIMITS.welcomeEmail))
        .messages(emailContentMessages(t)),
      attachments: Joi.array()
        .items(
          Joi.object({
            name: Joi.string(),
            size: Joi.number(),
            type: Joi.string(),
            url: Joi.string().optional(),
          }).unknown(true)
        )
        .max(4)
        .optional(),
      _files: Joi.any().optional(),
    })
      .unknown(true)
      // ".enabled" is this object's own key; "enabled" would be looked up on the whole form.
      .when(".enabled", {
        is: true,
        then: Joi.object({
          enabled: Joi.boolean().valid(true).required(),
          sendDelay: Joi.string()
            .valid("immediate", "1hour", "6hours", "1day", "2days")
            .required()
            .messages({
              "any.only": t(
                "customRegistration.validation.invalidSendDelay"
              ) as string,
              "any.required": t(
                "customRegistration.validation.sendDelayRequired"
              ) as string,
            }),
          // invalid("") takes back the empty value the base allows, which the merge would keep.
          subject: Joi.string()
            .invalid("")
            .min(3)
            .max(200)
            .required()
            .messages({
              "any.invalid": t(
                "customRegistration.validation.emailSubjectRequired"
              ) as string,
              "string.empty": t(
                "customRegistration.validation.emailSubjectRequired"
              ) as string,
              "string.min": t(
                "customRegistration.validation.emailSubjectMinLength"
              ) as string,
              "string.max": t(
                "customRegistration.validation.emailSubjectMaxLength"
              ) as string,
            }),
          // Merged with the base content rule, which keeps the maximum: only the minimum is added.
          content: Joi.string()
            .invalid("")
            .required()
            .custom(richTextRule(Number.POSITIVE_INFINITY, 10))
            .messages({
              ...emailContentMessages(t),
              "any.invalid": t(
                "customRegistration.validation.emailContentRequired"
              ) as string,
            }),
          attachments: Joi.array()
            .items(
              Joi.object({
                name: Joi.string(),
                size: Joi.number(),
                type: Joi.string(),
                url: Joi.string().optional(),
              }).unknown(true)
            )
            .max(4)
            .messages({
              "array.max": t(
                "customRegistration.validation.maxAttachmentsReached"
              ) as string,
            })
            .optional(),
          _files: Joi.any().optional(),
        }).unknown(true),
      }),
    esignature: Joi.object({
      enabled: Joi.boolean().required(),
      template_id: Joi.string().allow("").optional(),
      message: Joi.string().allow("").max(300).optional(),
      expiry_days: Joi.alternatives()
        .try(Joi.number(), Joi.string().allow(""))
        .optional(),
    }).unknown(true),
    booking: Joi.object({
      enabled: Joi.boolean().required(),
      type_id: Joi.when("enabled", {
        is: true,
        then: Joi.string()
          .required()
          .messages({
            "string.empty": t(
              "customRegistration.booking.validation.typeRequired"
            ) as string,
            "any.required": t(
              "customRegistration.booking.validation.typeRequired"
            ) as string,
          }),
        otherwise: Joi.string().allow(""),
      }),
    }).unknown(true),
  });
